import { useMemo } from 'react';
import { Link } from '@/shared/components/atoms/Link';
import { FlipLabel } from '@/shared/components/molecules/FlipLabel';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import {
  getOperatorEpochsQuery,
  getValidatorsAuthorityMembershipByEpochIds,
} from '@/shared/graphql/explorer/queries';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import useRemotePagination from '@/shared/hooks/useRemotePagination';
import { buildChainflipExplorerLink } from '@/shared/utils/chainflip';
import FlipAmount from '@/shared/utils/FlipAmount';
import { formatWithCommas } from '@/shared/utils/numbers';
import type TokenAmount from '@/shared/utils/TokenAmount';

type AuthoritySetRow = {
  authoritySet: number;
  numOfNodes: number;
  startBlockId?: number;
  endBlockId?: number;
  totalBonded: TokenAmount;
  validatorRewards: TokenAmount;
  operatorRewards: TokenAmount;
  delegatorRewards: TokenAmount;
  totalSlashed: TokenAmount;
};

const FlipCell = ({ flip }: { flip: TokenAmount }) => (
  <td>
    <div className="flex justify-start">
      <FlipLabel amount={flip} className="text-14" />
    </div>
  </td>
);

const Row = ({
  authoritySet,
  startBlockId,
  endBlockId,
  numOfNodes,
  totalBonded,
  validatorRewards,
  totalSlashed,
  operatorRewards,
  delegatorRewards,
}: AuthoritySetRow) => (
  <tr className="whitespace-nowrap">
    <td>
      <div className="flex flex-col gap-y-2 text-12">
        <Link
          className="underline"
          href={buildChainflipExplorerLink(authoritySet.toString(), 'authority-sets')}
          noPropagate
          target={process.env.NEXT_PUBLIC_EXPLORER_URL ? '_blank' : undefined}
        >
          Authority Set {authoritySet}
        </Link>
        <span className="flex items-center gap-x-1">
          {startBlockId && (
            <Link
              className="underline"
              href={buildChainflipExplorerLink(startBlockId.toString(), 'blocks')}
              noPropagate
              target={process.env.NEXT_PUBLIC_EXPLORER_URL ? '_blank' : undefined}
            >
              {formatWithCommas(startBlockId)}
            </Link>
          )}
          -
          {endBlockId ? (
            <Link
              className="underline"
              href={buildChainflipExplorerLink(endBlockId.toString(), 'blocks')}
              noPropagate
              target={process.env.NEXT_PUBLIC_EXPLORER_URL ? '_blank' : undefined}
            >
              {formatWithCommas(endBlockId)}
            </Link>
          ) : (
            <span>current</span>
          )}
        </span>
      </div>
    </td>
    <td>{numOfNodes}</td>
    <FlipCell flip={totalBonded} />
    <FlipCell flip={operatorRewards} />
    <FlipCell flip={validatorRewards} />
    <FlipCell flip={delegatorRewards} />
    <FlipCell flip={totalSlashed} />
  </tr>
);

export const AuthoritySetHistoryTable = ({
  operatorId,
}: {
  operatorId: `cF${string}`;
}): React.JSX.Element => {
  const {
    data: operatorEpochData,
    isLoading: isOperatorEpochDataLoading,
    paginationControls,
  } = useRemotePagination(getOperatorEpochsQuery, {
    variables: { idSs58: operatorId },
    context: { clientName: 'processor' },
  });

  const operatorEpochs = operatorEpochData?.allOperatorEpoches?.nodes ?? [];

  // collect epoch ids
  const epochIds = useMemo(() => operatorEpochs.map(({ epoch }) => epoch.id), [operatorEpochs]);

  // collect validator ids
  const validatorIds = useMemo(
    () => operatorEpochs.flatMap((epoch) => epoch.validators.nodes.map((v) => v.validatorId)),
    [operatorEpochs],
  );

  const { data: validatorsData, isLoading: isValidatorDataLoading } = useGqlQuery(
    getValidatorsAuthorityMembershipByEpochIds,
    {
      variables: { epochIds, ids: validatorIds },
      context: { clientName: 'processor' },
    },
  );

  const memberships = validatorsData?.allAuthorityMemberships?.nodes ?? [];

  const membershipMap = useMemo(
    () =>
      memberships.reduce((map, m) => {
        const key = m.epoch.id;
        let existing = map.get(key);

        if (!existing) {
          existing = {
            validatorRewards: new FlipAmount(0),
            totalSlashed: new FlipAmount(0),
          };
          map.set(key, existing);
        }

        existing.validatorRewards = existing.validatorRewards.add(m.totalRewards);
        existing.totalSlashed = existing.totalSlashed.add(
          m.epoch.slashedEvents.agg?.sum?.amount ?? 0,
        );

        map.set(key, existing);
        return map;
      }, new Map<number, { validatorRewards: TokenAmount; totalSlashed: TokenAmount }>()),
    [memberships],
  );

  const rows = useMemo(
    () =>
      operatorEpochs.map((epochNode) => {
        const membership = membershipMap.get(epochNode.epoch.id);
        return (
          <Row
            key={epochNode.epoch.id}
            authoritySet={epochNode.epoch.id}
            numOfNodes={epochNode.validators.numberOfNodes}
            startBlockId={epochNode.epoch.startBlockId}
            endBlockId={epochNode.epoch.endBlockId ?? undefined}
            totalBonded={new FlipAmount(epochNode.epoch.bond).mul(
              epochNode.validators.numberOfNodes,
            )}
            validatorRewards={membership?.validatorRewards ?? FlipAmount.ZERO}
            totalSlashed={membership?.totalSlashed ?? FlipAmount.ZERO}
            operatorRewards={new FlipAmount(epochNode.operatorRewardAmount)}
            delegatorRewards={new FlipAmount(epochNode.delegations.aggregates?.sum?.rewards ?? 0)}
          />
        );
      }),
    [operatorEpochs, membershipMap],
  );

  return (
    <TableV2
      columns={[
        'Authority Set / Blocks',
        '# of Nodes',
        'Total Bonded',
        'Commission Rewards',
        'Validator Rewards',
        'Delegator Rewards',
        'Total Slashed',
      ]}
      rows={rows}
      isLoading={isOperatorEpochDataLoading || isValidatorDataLoading}
      paginationControls={paginationControls}
    />
  );
};

export default AuthoritySetHistoryTable;
